import { getReportingDimensionValue, PAYMENT_DATE_DIMENSION } from "./reporting";
import { getClassificationAncestors } from "./hierarchy";

export const CHART_MEASURES = ["allocatedPrincipal", "allocatedInterest", "allocatedDebtService", "outstandingBalance"];
export const CATEGORY_ORDERS = ["valueDesc", "valueAsc", "labelAsc", "labelDesc"];

export function shouldResetBreakdown(view, categoryDimension, breakdownDimension) {
  return view === "category" && Boolean(breakdownDimension) && breakdownDimension === categoryDimension;
}

function recordValue(record, measure) {
  if (measure === "allocatedPrincipal") return Number(record.allocatedCents || 0) / 100;
  if (measure === "allocatedInterest") return Number(record.interestCents || 0) / 100;
  if (measure === "allocatedDebtService") return (Number(record.allocatedCents || 0) + Number(record.interestCents || 0)) / 100;
  return Number(record.outstandingBalanceCents || 0) / 100;
}

function aggregate(records, measure) {
  if (measure !== "outstandingBalance") return records.reduce((sum, record) => sum + recordValue(record, measure), 0);
  const latest = records.reduce((value, record) => String(record.balanceAsOfDate || "") > value ? String(record.balanceAsOfDate) : value, "");
  return records.reduce((sum, record) => String(record.balanceAsOfDate || "") === latest ? sum + recordValue(record, measure) : sum, 0);
}

function resolve(record, dimension, level, context) {
  return getReportingDimensionValue(record, dimension, context, level)
    || { key: "__unspecified", label: "Unspecified at this level" };
}

function resolveBreakdown(record, dimension, level, context) {
  const resolved = resolve(record, dimension, level, context);
  if (level !== 1 || resolved.key === "__unspecified") return resolved;
  const classification = context.classifications.find((item) => item.id === dimension);
  const valueId = record.classifications?.[dimension];
  const value = classification?.values?.find((item) => item.id === valueId);
  if (!classification || !value) return resolved;
  const path = [...getClassificationAncestors(classification.values, valueId), value];
  const parent = path[0];
  return parent && path[1]?.id === resolved.key
    ? { ...resolved, label: `${parent.name} - ${resolved.label}`, sortPath: [parent.name, resolved.label] }
    : resolved;
}

function compareSeries(left, right) {
  const leftPath = left.sortPath || [left.label];
  const rightPath = right.sortPath || [right.label];
  return leftPath[0].localeCompare(rightPath[0]) || (leftPath[1] || "").localeCompare(rightPath[1] || "");
}

function buildGroupedRows({ records, categoryDimension, categoryLevel, breakdownDimension, breakdownLevel, measure, context }) {
  const categoryMap = new Map();
  const seriesMap = new Map();
  records.forEach((record) => {
    const category = resolve(record, categoryDimension, categoryLevel, context);
    const group = categoryMap.get(category.key) || { ...category, records: [], bySeries: new Map() };
    group.records.push(record);
    if (breakdownDimension) {
      const series = resolveBreakdown(record, breakdownDimension, breakdownLevel, context);
      if (!seriesMap.has(series.key)) seriesMap.set(series.key, series);
      const seriesRecords = group.bySeries.get(series.key) || [];
      seriesRecords.push(record);
      group.bySeries.set(series.key, seriesRecords);
    }
    categoryMap.set(category.key, group);
  });
  const series = [...seriesMap.values()].sort(compareSeries);
  return {
    series,
    rows: [...categoryMap.values()].map((group) => ({
      id: group.key,
      label: group.label,
      value: aggregate(group.records, measure),
      seriesValues: Object.fromEntries(series.map((item) => [item.key, aggregate(group.bySeries.get(item.key) || [], measure)])),
    })),
  };
}

function limitSeries(grouped, maximum = 8) {
  const numericMaximum = maximum === "all" || maximum == null
    ? null
    : Math.max(1, Number(maximum) || 8);
  if (!numericMaximum || grouped.series.length <= numericMaximum) {
    return { ...grouped, hasOtherSeries: false, totalSeries: grouped.series.length };
  }
  const ranked = grouped.series.map((series) => ({
    ...series,
    total: grouped.rows.reduce((sum, row) => sum + Number(row.seriesValues[series.key] || 0), 0),
  })).sort((left, right) => right.total - left.total || left.label.localeCompare(right.label));
  const visible = ranked.slice(0, numericMaximum).sort(compareSeries);
  const hidden = ranked.slice(numericMaximum);
  return {
    series: [...visible, { key: "__other_series", label: `Other (${hidden.length})` }],
    rows: grouped.rows.map((row) => ({
      ...row,
      seriesValues: {
        ...Object.fromEntries(visible.map((series) => [series.key, row.seriesValues[series.key] || 0])),
        __other_series: hidden.reduce((sum, series) => sum + Number(row.seriesValues[series.key] || 0), 0),
      },
    })),
    hasOtherSeries: true,
    totalSeries: ranked.length,
  };
}

export function buildCapitalFlowCategoryChart({
  records = [], categoryDimension, categoryLevel = null,
  breakdownDimension = "", breakdownLevel = null,
  measure = "allocatedDebtService", limit = 12, seriesLimit = 8, categoryOrder = "valueDesc",
  projects = [], classifications = [],
}) {
  const grouped = limitSeries(buildGroupedRows({ records, categoryDimension, categoryLevel, breakdownDimension, breakdownLevel, measure, context: { projects, classifications } }), seriesLimit);
  const order = CATEGORY_ORDERS.includes(categoryOrder) ? categoryOrder : "valueDesc";
  const compareRows = {
    valueDesc: (left, right) => right.value - left.value || left.label.localeCompare(right.label),
    valueAsc: (left, right) => left.value - right.value || left.label.localeCompare(right.label),
    labelAsc: (left, right) => left.label.localeCompare(right.label),
    labelDesc: (left, right) => right.label.localeCompare(left.label),
  }[order];
  const ranked = grouped.rows.sort(compareRows);
  const numericLimit = limit === "all" || limit == null ? null : Math.max(1, Number(limit) || 12);
  if (!numericLimit || ranked.length <= numericLimit) return { ...grouped, rows: ranked, hasOther: false, totalCategories: ranked.length };
  const visible = ranked.slice(0, numericLimit);
  const hidden = ranked.slice(numericLimit);
  const other = {
    id: "__other", label: `Other (${hidden.length})`,
    value: hidden.reduce((sum, row) => sum + row.value, 0),
    seriesValues: Object.fromEntries(grouped.series.map((series) => [series.key, hidden.reduce((sum, row) => sum + Number(row.seriesValues[series.key] || 0), 0)])),
  };
  return { ...grouped, rows: [...visible, other], hasOther: true, totalCategories: ranked.length };
}

export function buildCapitalFlowTimeChart({
  records = [], breakdownDimension = "", breakdownLevel = null,
  measure = "allocatedDebtService", seriesLimit = 8, projects = [], classifications = [],
}) {
  const result = limitSeries(buildGroupedRows({
    records, categoryDimension: PAYMENT_DATE_DIMENSION, categoryLevel: null,
    breakdownDimension, breakdownLevel, measure, context: { projects, classifications },
  }), seriesLimit);
  return {
    ...result,
    rows: result.rows
      .sort((left, right) => left.label.localeCompare(right.label))
      .map((row) => ({ ...row, paymentDate: row.label })),
  };
}
